/**
 * SignalConfirmModal - 信号确认弹窗
 * 
 * 功能：
 * 1. 完成动作后弹出，确认预期信号是否已观测
 * 2. 每个信号可独立标记为已观测
 * 3. 信号确认后自动更新关联假设状态
 */
import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  CheckCircle, 
  Eye, 
  AlertCircle, 
  Loader2,
  CheckCircle2,
  ArrowRight
} from 'lucide-react'
import { clsx } from 'clsx'

interface PendingSignal {
  id: string
  strength: 'strong' | 'medium' | 'weak'
  description: string
  requiresConfirmation: boolean
}

interface SignalConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  actionContent: string
  pendingSignals: PendingSignal[]
  onObserveSignal: (signalId: string, notes?: string) => Promise<{
    hypothesisUpdated?: boolean
    hypothesisUpdate?: {
      hypothesisId: string
      oldStatus: string
      newStatus: string
    }
  }>
  onSkip?: () => void
}

// 信号强度配置
const SIGNAL_STRENGTH_CONFIG = {
  strong: { 
    label: '强信号', 
    color: 'text-green-700', 
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: '✅'
  },
  medium: { 
    label: '中信号', 
    color: 'text-amber-700', 
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    icon: '🔶'
  },
  weak: { 
    label: '弱信号', 
    color: 'text-gray-700', 
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: '⚪'
  },
}

export default function SignalConfirmModal({
  isOpen,
  onClose,
  actionContent,
  pendingSignals,
  onObserveSignal,
  onSkip,
}: SignalConfirmModalProps) {
  const [confirmedSignals, setConfirmedSignals] = useState<Set<string>>(new Set())
  const [loadingSignals, setLoadingSignals] = useState<Set<string>>(new Set())
  const [hypothesisUpdates, setHypothesisUpdates] = useState<Array<{
    signalId: string
    hypothesisId: string
    oldStatus: string
    newStatus: string
  }>>([])
  const [error, setError] = useState<string | null>(null)

  const handleConfirmSignal = useCallback(async (signalId: string) => {
    setLoadingSignals(prev => new Set(prev).add(signalId))
    setError(null)
    
    try {
      const result = await onObserveSignal(signalId)
      setConfirmedSignals(prev => new Set(prev).add(signalId))
      
      // 记录假设更新
      if (result.hypothesisUpdated && result.hypothesisUpdate) {
        setHypothesisUpdates(prev => [...prev, {
          signalId,
          ...result.hypothesisUpdate!
        }])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败，请重试')
    } finally {
      setLoadingSignals(prev => {
        const next = new Set(prev)
        next.delete(signalId)
        return next
      })
    }
  }, [onObserveSignal])

  const handleClose = () => {
    setConfirmedSignals(new Set())
    setLoadingSignals(new Set())
    setHypothesisUpdates([])
    setError(null)
    onClose()
  }

  const handleSkip = () => {
    onSkip?.()
    handleClose()
  }

  const allConfirmed = pendingSignals.every(s => confirmedSignals.has(s.id))
  const confirmedCount = confirmedSignals.size
  const totalCount = pendingSignals.length

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
              {/* 头部 */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-primary-600" />
                  <h3 className="font-semibold text-gray-900">信号确认</h3>
                  <span className="text-sm text-gray-500">
                    ({confirmedCount}/{totalCount})
                  </span>
                </div>
                <button
                  onClick={handleClose}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* 内容 */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* 动作内容 */}
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">动作已完成</span>
                  </div>
                  <p className="text-sm text-green-800 line-clamp-2">
                    {actionContent}
                  </p>
                </div>

                {/* 提示 */}
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
                  <span>
                    请确认以下预期信号是否已观测到。确认后将自动更新相关假设状态。
                  </span>
                </div>

                {/* 信号列表 */}
                <div className="space-y-3">
                  {pendingSignals.map((signal) => {
                    const config = SIGNAL_STRENGTH_CONFIG[signal.strength]
                    const isConfirmed = confirmedSignals.has(signal.id)
                    const isLoading = loadingSignals.has(signal.id)
                    const hypothesisUpdate = hypothesisUpdates.find(u => u.signalId === signal.id)

                    return (
                      <div
                        key={signal.id}
                        className={clsx(
                          'p-4 rounded-xl border-2 transition-all',
                          isConfirmed 
                            ? 'border-green-300 bg-green-50' 
                            : config.borderColor
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{config.icon}</span>
                              <span className={clsx(
                                'text-sm font-medium',
                                isConfirmed ? 'text-green-700' : config.color
                              )}>
                                {config.label}
                              </span>
                              {isConfirmed && (
                                <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                                  已确认
                                </span>
                              )}
                            </div>
                            <p className={clsx(
                              'text-sm',
                              isConfirmed ? 'text-green-800' : 'text-gray-700'
                            )}>
                              {signal.description}
                            </p>

                            {/* 假设更新提示 */}
                            {hypothesisUpdate && (
                              <div className="mt-2 flex items-center gap-2 text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded">
                                <ArrowRight className="w-3 h-3" />
                                <span>
                                  假设状态已更新：{hypothesisUpdate.oldStatus} → {hypothesisUpdate.newStatus}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* 确认按钮 */}
                          {!isConfirmed && (
                            <button
                              onClick={() => handleConfirmSignal(signal.id)}
                              disabled={isLoading}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {isLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle2 className="w-4 h-4" />
                                  确认观测
                                </>
                              )}
                            </button>
                          )}

                          {isConfirmed && (
                            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* 错误提示 */}
                {error && (
                  <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                {/* 完成提示 */}
                {allConfirmed && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-50 border border-green-200 rounded-xl text-center"
                  >
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-700">
                      所有信号已确认完毕！
                    </p>
                    {hypothesisUpdates.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        已自动更新 {hypothesisUpdates.length} 个假设状态
                      </p>
                    )}
                  </motion.div>
                )}
              </div>

              {/* 底部按钮 */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between gap-3 flex-shrink-0">
                <button
                  onClick={handleSkip}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  稍后确认
                </button>
                <button
                  onClick={handleClose}
                  className={clsx(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2',
                    allConfirmed
                      ? 'text-white bg-green-600 hover:bg-green-700'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  )}
                >
                  {allConfirmed ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      完成
                    </>
                  ) : (
                    '关闭'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
